import BaseParser from './BaseParser';

/**
 * InteractiveVisualizationParser - Parser genérico para visualizaciones interactivas
 *
 * Maneja dos formatos:
 * 1. [texto](url) donde url contiene el path específico
 * 2. [TYPE_VISUALIZATION][texto](url)[/TYPE_VISUALIZATION]
 */
class InteractiveVisualizationParser extends BaseParser {
  constructor(visualizationType, delimiters, urlPath, toolType) {
    super(visualizationType, 2); // Prioridad alta - se procesa antes que Mermaid
    this.delimiters = delimiters;
    this.urlPath = urlPath;
    this.toolType = toolType;
  }

  getPatterns() {
    return [
      new RegExp(`\\[${this.delimiters}\\]\\[([^\\]]+)\\]\\(([^)]+)\\)\\[\\/${this.delimiters}\\]`, 'g'), // Bloque con corchetes
      new RegExp(`\\[${this.delimiters}\\](.+?)\\(([^)]+)\\)\\[\\/${this.delimiters}\\]`, 'g'), // Bloque sin corchetes
      new RegExp(`\\[([^\\]]+)\\]\\(([^)]*${this.urlPath}[^)]+)\\)`, 'g'), // Markdown links
    ];
  }

  extractMatches(content) {
    const matches = [];
    const [blockPattern, blockNoBracketsPattern, markdownPattern] = this.getPatterns();
    const blockRanges = [];
    let match;

    // 1. Procesar bloques con corchetes primero
    blockPattern.lastIndex = 0;
    while ((match = blockPattern.exec(content)) !== null) {
      let url = match[2];
      let title = match[1];
      const start = match.index;
      const end = match.index + match[0].length;
      blockRanges.push([start, end]);

      // Limpiar sufijo raro en la url
      const htmlIndex = url.lastIndexOf('.html');
      if (htmlIndex !== -1 && url.length > htmlIndex + 5 && url[htmlIndex + 5] === ':') {
        url = url.substring(0, htmlIndex + 5);
      }
      // Limpiar emojis del título
      title = title.replace(/[👉📊💡]/gu, '').trim();

      matches.push({
        index: match.index,
        length: match[0].length,
        type: this.toolType,
        url: url,
        title: title,
      });
    }

    // 2. Procesar bloques sin corchetes, fuera de los bloques ya encontrados
    blockNoBracketsPattern.lastIndex = 0;
    while ((match = blockNoBracketsPattern.exec(content)) !== null) {
      const idx = match.index;
      const len = match[0].length;
      // Verificar si el match está dentro de algún bloque ya capturado
      const insideBlock = blockRanges.some(([start, end]) => idx >= start && idx < end);
      if (insideBlock) continue;

      let url = match[2];
      let title = match[1];
      // Limpiar sufijo raro en la url
      const htmlIndex = url.lastIndexOf('.html');
      if (htmlIndex !== -1 && url.length > htmlIndex + 5 && url[htmlIndex + 5] === ':') {
        url = url.substring(0, htmlIndex + 5);
      }
      // Limpiar emojis del título
      title = title.replace(/[👉📊💡]/gu, '').trim();

      matches.push({
        index: idx,
        length: len,
        type: this.toolType,
        url: url,
        title: title,
      });
    }

    // 3. Procesar enlaces markdown fuera de los bloques
    markdownPattern.lastIndex = 0;
    while ((match = markdownPattern.exec(content)) !== null) {
      const idx = match.index;
      const len = match[0].length;
      // Verificar si el match está dentro de algún bloque
      const insideBlock = blockRanges.some(([start, end]) => idx >= start && idx < end);
      if (insideBlock) continue;

      let url = match[2];
      let title = match[1];
      // Limpiar sufijo raro en la url
      const htmlIndex = url.lastIndexOf('.html');
      if (htmlIndex !== -1 && url.length > htmlIndex + 5 && url[htmlIndex + 5] === ':') {
        url = url.substring(0, htmlIndex + 5);
      }
      // Limpiar emojis del título
      title = title.replace(/[👉📊💡]/gu, '').trim();

      matches.push({
        index: idx,
        length: len,
        type: this.toolType,
        url: url,
        title: title,
      });
    }

    // 4. Ordenar matches por aparición
    matches.sort((a, b) => a.index - b.index);
    return matches;
  }

  createPart(match) {
    return {
      type: this.toolType,
      url: match.url,
      title: match.title,
    };
  }
}

export default InteractiveVisualizationParser;
